import React from 'react';
import { animated } from '@react-spring/web';
import { t } from '../../../utils/i18n';
import { baseXUnit, color, font, fontSize } from '../../../styles/theme';
import { useTestableTransition } from '../../module-overrides/react-spring';
import H from '../../elements/H';
import { IMTTripPhaseCities } from '../../../integrations/erp/apiTypes';
import IconText from '../IconText';
import Spacing from '../Spacing';
import {
  formatImtTripPhaseCitiesToStrings,
  getCityNameOfHotelPhase
} from './tripInfoUtils';
import TripsAvailable from './TripsAvailable';

interface TravelInfoBoxProps {
  visible: boolean;
  imtTripPhaseCities: IMTTripPhaseCities[];
  availabilityDate: string;
  shipTrip: boolean;
}

interface SlideTransitionProps {
  visible: boolean;
  children?: React.ReactNode;
}

const SlideTransition: React.FunctionComponent<SlideTransitionProps> = ({
  visible,
  children
}) => {
  const hiddenTransform = 'translate3d(0, 110%, 0)'; // 110% to hide the top box shadow
  const transitions = useTestableTransition(visible, {
    from: {
      transform: hiddenTransform
    },
    enter: {
      transform: 'translate3d(0, 0, 0)'
    },
    leave: {
      transform: hiddenTransform
    }
  });

  return (
    <div className="slide-animation-container">
      {transitions((style, item) => {
        return item && <animated.div style={style}>{children}</animated.div>;
      })}
    </div>
  );
};

const TravelInfoBox: React.FunctionComponent<TravelInfoBoxProps> = ({
  visible,
  imtTripPhaseCities,
  availabilityDate,
  shipTrip
}) => {
  const cityWithHotel = getCityNameOfHotelPhase(imtTripPhaseCities);
  const tripContentDataAvailable = imtTripPhaseCities.length;
  return (
    <SlideTransition visible={visible}>
      <div className="travel-info-box">
        <H styling="h5" color="gray">
          {t('Mistä hinta koostuu')}
        </H>
        {tripContentDataAvailable && (
          <div className="trip-content" data-testid="trip-content">
            {imtTripPhaseCities.length && (
              <div>
                <div className="trip-content-line">
                  <IconText
                    icon={shipTrip ? 'travel-ship' : 'travel-plane'}
                    iconSize={2}
                    data-testid="trip-cities"
                  >
                    <div>
                      {formatImtTripPhaseCitiesToStrings(
                        imtTripPhaseCities
                      ).map((line) => (
                        <div>{line}</div>
                      ))}
                    </div>
                  </IconText>
                </div>
                {cityWithHotel && (
                  <div className="trip-content-line">
                    <IconText
                      icon="travel-hotel"
                      iconSize={2}
                      data-cy="trip-hotel"
                      data-testid="trip-hotel"
                    >
                      {t('Kohteessa')} {cityWithHotel}
                    </IconText>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <Spacing top={2}>
          <TripsAvailable date={availabilityDate} small />
        </Spacing>
      </div>
      {/*language=CSS*/}
      <style jsx>{`
        .travel-info-box {
          min-height: ${baseXUnit(
            18
          )}; // TODO: Adjust/remove after there is proper content here
          width: 100%;
          background-color: ${color.white};
          box-shadow:
            0 0 10px 0 rgba(0, 0, 0, 0.16),
            0 -8px 10px 0 rgba(0, 0, 0, 0.1);
          padding: ${baseXUnit(2)};
          border-top-left-radius: ${baseXUnit(1)};
          border-top-right-radius: ${baseXUnit(1)};
        }
        .travel-info-heading {
          color: ${color.gray};
        }
        .trip-content {
          background-color: ${color.poolVeryLight};
          margin-top: ${baseXUnit(1)};
          padding: ${baseXUnit(1)} ${baseXUnit(1)} 0;
          padding: ${baseXUnit(1)};
          font-family: ${font.mono};
          font-size: ${fontSize.px14};
        }
        .trip-content-line {
          margin-bottom: ${baseXUnit(1)};
        }
        .trip-content-line:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </SlideTransition>
  );
};

export default TravelInfoBox;
